import { Navigate, Route, Routes } from 'react-router';
import {
  ADMIN_KEYS_PATH,
  ADMIN_NEW_CREDENTIAL_PATH,
  ADMIN_NEW_SOURCE_PATH,
  ADMIN_PATH,
  ADMIN_REPOSITORIES_PATH,
  ADMIN_SYNC_PATH,
  ADMIN_USERS_PATH,
  AGENTS_PATH,
  AUTH_CALLBACK_PATH,
  LEGACY_ADMIN_NEW_CREDENTIAL_PATH,
  LEGACY_ADMIN_NEW_SOURCE_PATH,
  LOGIN_PATH,
  LUNA_TELEMETRY_PATH,
  SDLC_PATH,
  SEARCH_PATH,
  TOOLKIT_PATH,
} from './config';
import { RequireAdmin } from './auth/RequireAdmin';
import { RequireSdlcAccess } from './auth/RequireSdlcAccess';
import { ErrorBoundary } from './components/ErrorBoundary';
import { KeepLocationRedirect } from './components/KeepLocationRedirect';
import { AdminLayout } from './layouts/AdminLayout';
import { ProtectedLayout } from './layouts/ProtectedLayout';
import { AdminCredentialNewPage } from './pages/AdminCredentialNewPage';
import { AdminKeysPage } from './pages/AdminKeysPage';
import { AdminRepositoriesPage } from './pages/AdminRepositoriesPage';
import { AdminSourceNewPage } from './pages/AdminSourceNewPage';
import { AdminSyncPage } from './pages/AdminSyncPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AgentLibraryPage } from './pages/AgentLibraryPage';
import { AgentPage } from './pages/AgentPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { LifecyclePage } from './pages/LifecyclePage';
import { LoginPage } from './pages/LoginPage';
import { MethodPage } from './pages/MethodPage';
import { LunaTelemetryPage } from './pages/common/LunaTelemetryPage';
import { NotFoundPage } from './pages/common/NotFoundPage';
import { PhasePage } from './pages/PhasePage';
import { ProcessPage } from './pages/ProcessPage';
import { SdlcLayout } from './layouts/SdlcLayout';
import { SearchPage } from './pages/SearchPage';
import { useAppSelector } from './store/hooks';
import { ThemeModeProvider } from './theme';

// `/` is not a page of its own — it redirects wherever this session's landing
// spot is. SDLC when the catalog is open to it, Toolkit otherwise: the
// alternative, redirecting into SDLC and immediately bouncing off
// RequireSdlcAccess, would be a redirect loop dressed as a home page.
const LandingRedirect = () => {
  const sdlcAccess = useAppSelector((state) => state.auth.user?.sdlcAccess ?? false);
  return <Navigate to={sdlcAccess ? SDLC_PATH : TOOLKIT_PATH} replace />;
};

// **ThemeModeProvider owns the theme and the baseline**, and mounts them in that
// order — CssBaseline reads the theme to paint the page background and the base
// type, so outside the provider it would emit MUI's defaults and the whole app
// would sit on the wrong ground.
//
// Two routes are public and the rest are not. The split is structural rather
// than per-route: everything nested under ProtectedLayout inherits the guard, so
// a page added later is protected unless someone deliberately moves it out.
//
// /login and the callback have to be public. A guard on either is a redirect
// loop — the callback especially, since at the moment it runs there is by
// definition no session yet.
//
// **The `/sdlc` routes mirror a record's `path` exactly**, one segment per
// level: `experience-design`, then `/user-research`, then `/field-study`. That
// is what lets a card build its link as `${SDLC_PATH}/${record.path}` with no
// reshaping, and what lets a page rejoin its params into the path the API is
// keyed on. A stage is not a segment — it groups phases, it is not a level.
// **The boundary is inside the theme and outside the Routes.** Inside, because
// what it renders on a crash is themed like everything else; outside the routes,
// because a throw during a route's render has already unmounted that route — a
// boundary within it would go down with the thing it was meant to catch. It is
// the last frame standing, which is the only useful place for it.
export const App = () => (
  <ThemeModeProvider>
    <ErrorBoundary>
      <Routes>
        <Route path={LOGIN_PATH} element={<LoginPage />} />
        <Route path={AUTH_CALLBACK_PATH} element={<AuthCallbackPage />} />

        <Route element={<ProtectedLayout />}>
          {/* The catalog is the app. `/` is an address people will type and a
              bookmark people already hold, so it redirects rather than 404s. */}
          <Route path="/" element={<LandingRedirect />} />

          {/* SDLC and search both need sdlcAccess — search finds only SDLC
              content, so a session without it has nothing to search either.
              One guard over both is what lets AppBar's nav-hiding and this
              stay in agreement without being taught about each other. */}
          <Route element={<RequireSdlcAccess />}>
            <Route path={SDLC_PATH} element={<SdlcLayout />}>
              <Route index element={<LifecyclePage />} />
              <Route path=":phaseSlug" element={<PhasePage />} />
              <Route path=":phaseSlug/:processSlug" element={<ProcessPage />} />
              <Route path=":phaseSlug/:processSlug/:methodSlug" element={<MethodPage />} />
            </Route>

            <Route path={SEARCH_PATH} element={<SearchPage />} />
          </Route>

          {/* The Agent Skill library is the Toolkit, open to every signed-in session:
              its routes on the content service carry no scope. `/toolkit/agents`
              was the library's address and is kept as a redirect for bookmarks. */}
          <Route path={TOOLKIT_PATH} element={<AgentLibraryPage />} />
          <Route path={AGENTS_PATH} element={<Navigate to={TOOLKIT_PATH} replace />} />
          <Route path={`${AGENTS_PATH}/:name`} element={<AgentPage />} />
          <Route path={LUNA_TELEMETRY_PATH} element={<LunaTelemetryPage />} />

          {/* The admin area. One guard over all of it, as SDLC and search share one: the
              routes behind these pages need the admin scope at the gateway, and this keeps
              everyone else from landing on a page of refused requests. `/admin` goes to the
              first page, and the two add pages keep their old addresses as redirects. */}
          <Route element={<RequireAdmin />}>
            <Route path={ADMIN_PATH} element={<AdminLayout />}>
              <Route index element={<Navigate to={ADMIN_REPOSITORIES_PATH} replace />} />
              <Route path={ADMIN_REPOSITORIES_PATH} element={<AdminRepositoriesPage />} />
              <Route path={ADMIN_KEYS_PATH} element={<AdminKeysPage />} />
              <Route path={ADMIN_USERS_PATH} element={<AdminUsersPage />} />
              <Route path={ADMIN_SYNC_PATH} element={<AdminSyncPage />} />
            </Route>
            <Route path={ADMIN_NEW_SOURCE_PATH} element={<AdminSourceNewPage />} />
            <Route path={ADMIN_NEW_CREDENTIAL_PATH} element={<AdminCredentialNewPage />} />
            <Route path={LEGACY_ADMIN_NEW_SOURCE_PATH} element={<KeepLocationRedirect to={ADMIN_NEW_SOURCE_PATH} />} />
            <Route path={LEGACY_ADMIN_NEW_CREDENTIAL_PATH} element={<KeepLocationRedirect to={ADMIN_NEW_CREDENTIAL_PATH} />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  </ThemeModeProvider>
);
